import { makeClientTool } from "@zapier/ai-toolkit";
import { Tool } from "ai";

export type ToolWithInclude = Tool & {
  shouldInclude?: () => boolean;
};

/**
 * Adapts agent-system tools to ai-toolkit ClientTool format
 *
 * This utility converts the existing Canvas tools from the @zapier/agent-system
 * format to the @zapier/ai-toolkit format for feature-flagged migration.
 *
 * Key differences:
 * - agent-system: tools are Record<string, ToolWithInclude> with execute returning content wrapper
 * - ai-toolkit: tools are ClientTool[] with execute returning plain objects
 */
export const adaptCanvasToolsToAiToolkit = (
  tools: Record<string, ToolWithInclude>,
) => {
  return Object.entries(tools)
    .filter(([, tool]) => {
      // Apply shouldInclude logic if present
      if (tool.shouldInclude) {
        return tool.shouldInclude();
      }
      return true;
    })
    .map(([toolName, tool]) => {
      // If the tool already has a component property, it's already a ClientTool
      // Pass it through directly instead of re-wrapping it
      if ("component" in tool && tool.component) {
        return tool as any; // Already a ClientTool with component
      }

      return makeClientTool({
        name: toolName,
        description: tool.description || "",
        parameters: tool.parameters,
        execute: async (args: any) => {
          try {
            // Call the original execute function
            const result = await tool.execute?.(args, {
              toolCallId: "",
              messages: [],
            });

            // Extract the text content from the agent-system response format
            if (result && result.content && Array.isArray(result.content)) {
              const textContent = result.content
                .filter((item: any) => item.type === "text")
                .map((item: any) => item.text)
                .join("\n");

              return {
                success: true,
                result: textContent,
                originalResponse: result,
              };
            }

            // Fallback for unexpected response format
            return {
              success: true,
              result: JSON.stringify(result, null, 2),
              originalResponse: result,
            };
          } catch (error) {
            console.error(`Error executing adapted tool ${toolName}:`, error);
            return {
              success: false,
              error: error instanceof Error ? error.message : String(error),
              toolName,
            };
          }
        },
        // Optional: Add a simple text component for displaying results
        // component: ({ result }) => {
        //   if (!result) return null;

        //   return (
        //     <div className="canvas-tool-result">
        //       {result.success ? (
        //         <div className="tool-success">
        //           <pre>{result.result}</pre>
        //         </div>
        //       ) : (
        //         <div className="tool-error">
        //           <strong>Error in {result.toolName}:</strong> {result.error}
        //         </div>
        //       )}
        //     </div>
        //   );
        // },
      });
    });
};
